import { Suspense, type ReactNode } from 'react';
import { Loader2, LogOut } from 'lucide-react';
import { AppHeader, type AppHeaderProps } from './app-header';
import { ErrorBoundary } from './error-boundary';
import type { FeedbackFormProps } from './feedback-form';
import { useMsalAccount, type MsalAccountSource } from '../hooks/use-msal-account';
import { Button } from './ui/button';
import { Toaster } from './ui/sonner';

/** Literal class names, so Tailwind's scanner sees them. Header and <main> share one. */
const WIDTHS = {
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
} as const;

export interface AppShellProps {
  /** App name beside the logo; also titles the help panel. */
  title: string;
  subtitle?: string;
  /** Content width for header and page. Defaults to 4xl. */
  width?: keyof typeof WIDTHS;
  /** The app's MSAL instance; the signed-in account it holds is shown in the toolbar. */
  msal: MsalAccountSource<{ name?: string; username: string; localAccountId: string }>;
  onSignOut: () => void;
  /** The app's docs/USERS.md, as a string (`?raw` import). */
  help: string;
  /**
   * Report a problem / Suggest (#511), left of Help: the app's `sendFeedback` and its catalogue
   * key. The signed-in account comes from `msal`. Every app but the portal passes it.
   */
  feedback?: Omit<FeedbackFormProps, 'accountId'>;
  /** Extra toolbar buttons, placed before sign-out (e.g. the portal's Settings gear). */
  toolbar?: ReactNode;
  /** A row under the header bar, e.g. the app's section tabs. */
  subNav?: ReactNode;
  /** The waffle back to the portal; only the portal turns it off. */
  launcher?: boolean;
  /** The title's link home — see AppHeader; react-router apps pass `(p) => <Link to="/" {...p} />`. */
  homeLink?: AppHeaderProps['homeLink'];
  /** Build stamp for the footer (the app's VITE_APP_VERSION). */
  version?: string;
  /** Spinner text while a page suspends. Defaults to "Loading…". */
  loadingText?: string;
  /** The page — usually react-router's <Outlet />. */
  children: ReactNode;
}

/**
 * Every web app's frame (#314), previously a copy per app's _layout.tsx:
 * header with the signed-in user, sign-out and help (rightmost, per the
 * in-app help rule), the page inside an error boundary and a spinner for pages
 * that suspend on first render, a build stamp and the toast host.
 */
export function AppShell({
  title,
  subtitle,
  width = '4xl',
  msal,
  onSignOut,
  help,
  feedback,
  toolbar,
  subNav,
  launcher,
  homeLink,
  version,
  loadingText = 'Loading…',
  children,
}: AppShellProps) {
  const account = useMsalAccount(msal);
  const widthClass = WIDTHS[width];
  return (
    // min-h-dvh (not min-h-screen) so mobile browser chrome doesn't clip the page.
    <div className="min-h-dvh bg-gradient-to-br from-slate-50 via-slate-100 to-orange-50">
      <AppHeader
        title={title}
        subtitle={subtitle}
        containerClassName={widthClass}
        launcher={launcher}
        subNav={subNav}
        homeLink={homeLink}
        feedback={feedback && { ...feedback, accountId: account?.localAccountId }}
        help={{ source: help, title: `${title} — Help` }}
      >
        {account && (
          <span
            className="hidden max-w-[160px] truncate text-xs text-slate-500 sm:block"
            title={account.username}
          >
            {account.name ?? account.username}
          </span>
        )}
        {toolbar}
        <Button
          variant="ghost"
          size="sm"
          onClick={onSignOut}
          className="text-slate-500 hover:text-slate-900"
          title={`Sign out ${account?.username ?? ''}`.trim()}
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </AppHeader>

      <main className={`mx-auto ${widthClass} px-4 pb-16 pt-6 sm:px-6`}>
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> {loadingText}
              </div>
            }
          >
            {children}
          </Suspense>
        </ErrorBoundary>
      </main>

      {version && <footer className="pb-3 text-center text-[10px] text-slate-400">{version}</footer>}

      <Toaster position="top-center" />
    </div>
  );
}
